import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faMagnifyingGlass,
  faChevronLeft,
  faChevronRight,
  faBan,
  faRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import { useAxiosInstance } from "../../../services/Interceptor";
import {
  getDuplicateAthletes,
  dismissDuplicatePair,
  restoreDuplicatePair,
  combineAthleteAccounts,
} from "../../../services/AdminService";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import DuplicateUsersMobileList from "./DuplicateUsersMobileList";
import DuplicateAccountCompareModal from "./DuplicateAccountCompareModal";

const PAGE_SIZE = 25;

export const COLUMNS = [
  { key: "username", label: "Login Email" },
  { key: "firstName", label: "First Name" },
  { key: "lastName", label: "Last Name" },
  { key: "primaryPosition", label: "Primary Position" },
  { key: "state", label: "Home State" },
  { key: "athleteEmail", label: "Athlete Email" },
  { key: "athletePhone", label: "Athlete Phone" },
  { key: "parentEmail", label: "Parent Email" },
  { key: "parentPhone", label: "Parent Phone" },
  { key: "expectedYearOfGraduation", label: "Graduation Year" },
];

export const normalizeValue = (value) => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed === "" ? null : trimmed.toLowerCase();
};

export const computeGroupMatches = (members, columns) => {
  const matches = {};
  columns.forEach((col) => {
    const values = (members ?? [])
      .map((m) => normalizeValue(m?.[col.key]))
      .filter((v) => v !== null);
    matches[col.key] = new Set(values).size <= 1;
  });
  return matches;
};

export const getPairIds = (pair) => [
  pair.lowerUserId ?? pair.firstUserId ?? pair.userIdA ?? pair.userId1,
  pair.higherUserId ?? pair.secondUserId ?? pair.userIdB ?? pair.userId2,
];

export const isPairDismissed = (group, idA, idB) =>
  (group.dismissedPairs ?? []).some((pair) => {
    const [a, b] = getPairIds(pair);
    return (a === idA && b === idB) || (a === idB && b === idA);
  });

export const extractBackendMessage = (payload, fallback) => {
  if (typeof payload === "string" && payload.trim()) return payload;
  if (payload && typeof payload === "object") {
    if (typeof payload.message === "string" && payload.message.trim()) {
      return payload.message;
    }
    if (typeof payload.error === "string" && payload.error.trim()) {
      return payload.error;
    }
    if (typeof payload.data === "string" && payload.data.trim()) {
      return payload.data;
    }
  }
  return fallback;
};

const withGroupMeta = (group) => ({
  ...group,
  groupKey: `${group.firstName ?? ""}-${group.lastName ?? ""}-${(
    group.members ?? []
  )
    .map((m) => m.userId)
    .join("-")}`,
  matches: computeGroupMatches(group.members, COLUMNS),
});

export default function AdminDuplicateUsers() {
  const apiInstance = useAxiosInstance();
  const [groups, setGroups] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebouncedValue(searchQuery, 300);
  const [showDismissed, setShowDismissed] = useState(false);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [selection, setSelection] = useState({ groupKey: null, ids: [] });
  const [compareModal, setCompareModal] = useState({
    open: false,
    primary: null,
    secondary: null,
  });
  const [dismissing, setDismissing] = useState(false);
  const [merging, setMerging] = useState(false);
  const [mergeError, setMergeError] = useState(null);
  const [restoring, setRestoring] = useState(false);

  const fetchData = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const data = await getDuplicateAthletes(
        apiInstance,
        debouncedSearch,
        page,
        PAGE_SIZE,
        showDismissed,
      );
      setGroups((data.results ?? []).map(withGroupMeta));
      setTotalPages(data.totalPages ?? 1);
      setTotalResults(data.totalResults ?? 0);
    } catch (err) {
      setError(
        typeof err === "string" ? err : (err?.message ?? "Failed to load data"),
      );
    } finally {
      setLoading(false);
    }
  }, [apiInstance, page, debouncedSearch, showDismissed]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset to page 1 on a new search or filter, rather than an out-of-range page.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, showDismissed]);

  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  const renderSortIcon = (col) => {
    if (sortCol !== col)
      return (
        <FontAwesomeIcon icon={faSort} className="ml-1 text-gray-400 text-xs" />
      );
    return (
      <FontAwesomeIcon
        icon={sortDir === "asc" ? faSortUp : faSortDown}
        className="ml-1 text-red-600 text-xs"
      />
    );
  };

  const sortedGroups = (() => {
    if (!sortCol) return groups;
    return [...groups].sort((a, b) => {
      const av = a.members?.[0]?.[sortCol] ?? "";
      const bv = b.members?.[0]?.[sortCol] ?? "";
      const cmp = String(av).localeCompare(String(bv), undefined, {
        numeric: true,
      });
      return sortDir === "asc" ? cmp : -cmp;
    });
  })();

  const clearSelection = () => setSelection({ groupKey: null, ids: [] });

  const handleToggleMember = (group, userId) => {
    const isSameGroup = selection.groupKey === group.groupKey;
    let nextIds;
    if (!isSameGroup) {
      nextIds = [userId];
    } else if (selection.ids.includes(userId)) {
      nextIds = selection.ids.filter((id) => id !== userId);
    } else if (selection.ids.length >= 2) {
      nextIds = [selection.ids[1], userId];
    } else {
      nextIds = [...selection.ids, userId];
    }

    if (nextIds.length === 2) {
      const memberA = group.members.find((m) => m.userId === nextIds[0]);
      const memberB = group.members.find((m) => m.userId === nextIds[1]);
      if (
        memberA &&
        memberB &&
        isPairDismissed(group, memberA.userId, memberB.userId)
      ) {
        toast.info(
          "This pair was already marked as not a duplicate. Use Restore to bring it back.",
          { position: "top-right" },
        );
        setSelection({ groupKey: null, ids: [] });
        return;
      }
    }

    setSelection({ groupKey: group.groupKey, ids: nextIds });
  };

  const closeCompareModal = () => {
    setCompareModal({ open: false, primary: null, secondary: null });
    setMergeError(null);
    clearSelection();
  };

  const handleOpenCompare = (group) => {
    if (selection.groupKey !== group.groupKey || selection.ids.length !== 2) {
      return;
    }
    const memberA = group.members.find((m) => m.userId === selection.ids[0]);
    const memberB = group.members.find((m) => m.userId === selection.ids[1]);
    if (!memberA || !memberB) return;
    const primary = memberA.userId > memberB.userId ? memberA : memberB;
    const secondary = memberA.userId > memberB.userId ? memberB : memberA;
    setMergeError(null);
    setCompareModal({ open: true, primary, secondary });
  };

  const handleMergePair = async () => {
    const { primary, secondary } = compareModal;
    if (!primary || !secondary) return;
    setMerging(true);
    setMergeError(null);
    try {
      const result = await combineAthleteAccounts(
        apiInstance,
        primary.userId,
        secondary.userId,
      );
      // Route returns an empty body today, so the fallback names both accounts.
      toast.success(
        extractBackendMessage(
          result,
          `Merged ${secondary.firstName} ${secondary.lastName} into ${primary.firstName} ${primary.lastName}.`,
        ),
        { position: "top-right" },
      );
      closeCompareModal();
      await fetchData();
    } catch (err) {
      const message = extractBackendMessage(
        err,
        "Failed to merge these accounts.",
      );
      setMergeError(message);
      toast.error(message, { position: "top-right" });
    } finally {
      setMerging(false);
    }
  };

  const handleDismissFromGroup = async (group) => {
    if (selection.groupKey !== group.groupKey || selection.ids.length !== 2) {
      return;
    }
    const [idA, idB] = selection.ids;
    setDismissing(true);
    try {
      const result = await dismissDuplicatePair(apiInstance, idA, idB);
      toast.success(
        extractBackendMessage(result, "Marked as not a duplicate."),
        { position: "top-right" },
      );
      clearSelection();
      await fetchData();
    } catch (err) {
      toast.error(
        extractBackendMessage(
          err,
          "Failed to mark this pair as not a duplicate.",
        ),
        { position: "top-right" },
      );
    } finally {
      setDismissing(false);
    }
  };

  const handleRestore = async (idA, idB) => {
    setRestoring(true);
    try {
      const result = await restoreDuplicatePair(apiInstance, idA, idB);
      toast.success(extractBackendMessage(result, "Pair restored."), {
        position: "top-right",
      });
      await fetchData();
    } catch (err) {
      toast.error(extractBackendMessage(err, "Failed to restore this pair."), {
        position: "top-right",
      });
    } finally {
      setRestoring(false);
    }
  };

  const startRecord = totalResults === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endRecord = Math.min(page * PAGE_SIZE, totalResults);

  const renderPagination = () => {
    if (totalResults === 0) return null;

    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push("...");
      for (
        let i = Math.max(2, page - 1);
        i <= Math.min(totalPages - 1, page + 1);
        i++
      ) {
        pages.push(i);
      }
      if (page < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }

    return (
      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm flex-wrap gap-2">
        <span className="text-gray-600">
          Showing {startRecord}&ndash;{endRecord} of {totalResults} results
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage((p) => p - 1)}
            disabled={page === 1}
            className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          {pages.map((p, i) =>
            p === "..." ? (
              <span key={`ellipsis-${i}`} className="px-2 text-gray-500">
                &hellip;
              </span>
            ) : (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`px-3 py-1 rounded border ${
                  p === page
                    ? "bg-red-500 text-white border-red-500"
                    : "border-gray-300 hover:bg-gray-100"
                }`}
              >
                {p}
              </button>
            ),
          )}
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page === totalPages}
            className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>
    );
  };

  const totalCols = COLUMNS.length + 1;

  return (
    <div className="overflow-y-auto overflow-x-hidden h-full p-4 bg-white lg:bg-gray-100">
      <div className="hidden lg:block min-w-0">
        <div className="bg-white shadow-lg rounded-lg p-4">
          <h1 className="text-xl mb-5 uppercase text-gray-800">
            POTENTIAL DUPLICATE ACCOUNTS
          </h1>

          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={showDismissed}
                onChange={(e) => setShowDismissed(e.target.checked)}
              />
              Show dismissed
            </label>

            <div className="relative">
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"
              />
              <input
                type="text"
                placeholder="Name/Email"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-8 pr-3 py-2 border border-gray-300 rounded text-sm w-52 focus:outline-none focus:ring-1 focus:ring-red-400"
              />
            </div>
          </div>

          {loading && (
            <div className="text-center py-10 text-gray-500">Loading...</div>
          )}
          {error && !loading && (
            <div className="text-center py-10 text-red-500">{error}</div>
          )}

          {!loading && !error && (
            <div className="bg-white rounded-lg shadow">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-red-100 border-b border-red-200">
                      <th className="px-3 py-3 w-10">
                        <span className="sr-only">Select</span>
                      </th>
                      {COLUMNS.map((col) => (
                        <th
                          key={col.key}
                          onClick={() => handleSort(col.key)}
                          className="px-3 py-3 text-left font-semibold text-gray-700 cursor-pointer select-none text-sm"
                        >
                          <div className="flex flex-col">
                            <span>{col.label}</span>
                            <span>{renderSortIcon(col.key)}</span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedGroups.length === 0 ? (
                      <tr>
                        <td
                          colSpan={totalCols}
                          className="text-center py-10 text-gray-500"
                        >
                          No results found
                        </td>
                      </tr>
                    ) : (
                      sortedGroups.map((group, gi) => {
                        const shade = gi % 2 === 0 ? "bg-white" : "bg-gray-50";
                        const hasPairSelected =
                          selection.groupKey === group.groupKey &&
                          selection.ids.length === 2;
                        return (
                          <React.Fragment key={group.groupKey}>
                            <tr
                              className={`${shade} border-t-2 border-gray-300`}
                            >
                              <td colSpan={totalCols} className="px-3 py-2">
                                <span className="font-semibold text-gray-800">
                                  {group.firstName} {group.lastName}
                                  <span className="ml-2 text-xs text-gray-500 font-normal">
                                    {group.members?.length ?? 0} accounts
                                  </span>
                                </span>
                              </td>
                            </tr>
                            {hasPairSelected && (
                              <tr className="bg-red-50 border-b border-red-200">
                                <td colSpan={totalCols} className="px-3 py-2.5">
                                  <div className="flex items-center justify-center gap-3">
                                    <span className="text-xs text-gray-600 font-medium">
                                      2 accounts selected
                                    </span>
                                    <button
                                      onClick={() =>
                                        handleDismissFromGroup(group)
                                      }
                                      disabled={dismissing}
                                      className="text-sm font-medium px-4 py-1.5 rounded border border-gray-400 bg-white hover:bg-gray-100 disabled:opacity-50"
                                    >
                                      Not a duplicate
                                    </button>
                                    <button
                                      onClick={() => handleOpenCompare(group)}
                                      className="text-sm font-semibold px-4 py-1.5 rounded bg-red-500 text-white shadow-sm hover:bg-red-600"
                                    >
                                      Compare &amp; Merge
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )}
                            {group.members?.map((member) => (
                              <tr
                                key={member.userId}
                                className={`${shade} border-b border-gray-100 hover:bg-gray-100 transition-colors`}
                              >
                                <td className="px-3 py-1">
                                  <input
                                    type="checkbox"
                                    checked={
                                      selection.groupKey === group.groupKey &&
                                      selection.ids.includes(member.userId)
                                    }
                                    onChange={() =>
                                      handleToggleMember(group, member.userId)
                                    }
                                  />
                                </td>
                                {COLUMNS.map((col) => (
                                  <td
                                    key={col.key}
                                    className={`px-3 py-1 text-gray-800 border ${
                                      group.matches[col.key]
                                        ? "bg-green-50 border-green-200"
                                        : "bg-red-50 border-red-200"
                                    }`}
                                  >
                                    {member[col.key] ?? ""}
                                  </td>
                                ))}
                              </tr>
                            ))}
                            {showDismissed &&
                              group.dismissedPairs?.map((pair, i) => {
                                const [idA, idB] = getPairIds(pair);
                                return (
                                  <tr
                                    key={`dismissed-${idA}-${idB}-${i}`}
                                    className="bg-gray-50 border-b border-gray-200"
                                  >
                                    <td
                                      colSpan={totalCols}
                                      className="px-3 py-1.5"
                                    >
                                      <div className="flex items-center justify-between">
                                        <span className="flex items-center gap-2 text-xs text-gray-500">
                                          <FontAwesomeIcon
                                            icon={faBan}
                                            className="text-gray-400"
                                          />
                                          Dismissed:
                                          <span className="px-1.5 py-0.5 rounded bg-white border border-gray-200 font-medium text-gray-600">
                                            #{idA}
                                          </span>
                                          &harr;
                                          <span className="px-1.5 py-0.5 rounded bg-white border border-gray-200 font-medium text-gray-600">
                                            #{idB}
                                          </span>
                                        </span>
                                        <button
                                          onClick={() =>
                                            handleRestore(idA, idB)
                                          }
                                          disabled={restoring}
                                          className="flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full border border-blue-300 text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                                        >
                                          <FontAwesomeIcon
                                            icon={faRotateLeft}
                                          />
                                          Restore
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              {renderPagination()}
            </div>
          )}
        </div>
      </div>

      <div className="lg:hidden">
        <DuplicateUsersMobileList
          groups={sortedGroups}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          showDismissed={showDismissed}
          onShowDismissedChange={setShowDismissed}
          loading={loading}
          error={error}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          startRecord={startRecord}
          endRecord={endRecord}
          onPageChange={setPage}
          selection={selection}
          onToggleMember={handleToggleMember}
          onRestore={handleRestore}
          restoring={restoring}
          onDismissSelected={handleDismissFromGroup}
          onOpenCompare={handleOpenCompare}
          dismissing={dismissing}
        />
      </div>

      <DuplicateAccountCompareModal
        open={compareModal.open}
        primary={compareModal.primary}
        secondary={compareModal.secondary}
        merging={merging}
        mergeError={mergeError}
        onClose={closeCompareModal}
        onMerge={handleMergePair}
      />
    </div>
  );
}
